import { supabase } from '../lib/supabase';

// Ensure user is authenticated; throw with a readable message if not.
const requireUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be logged in to save settings.');
  }
  return user;
};

// Get default tax rate from database (defaults to 11%)
export const getDefaultTaxRate = async () => {
  try {
    const { data, error } = await supabase
      .from('application_settings')
      .select('setting_value')
      .eq('setting_key', 'default_tax_rate')
      .single();

    if (error) {
      // If setting doesn't exist, return default
      if (error.code === 'PGRST116') {
        return 11;
      }
      throw error;
    }

    return parseFloat(data?.setting_value) || 11;
  } catch (error) {
    console.error('Error getting default tax rate:', error);
    // Fallback to 11%
    return 11;
  }
};

// Save default tax rate to database (requires auth; uses application_settings table)
export const saveDefaultTaxRate = async (taxRate) => {
  await requireUser();
  const num = typeof taxRate === 'number' ? taxRate : parseFloat(taxRate, 10);
  if (Number.isNaN(num) || num < 0 || num > 100) {
    throw new Error('Tax rate must be a number between 0 and 100.');
  }
  try {
    const { error } = await supabase
      .from('application_settings')
      .upsert({
        setting_key: 'default_tax_rate',
        setting_value: String(num),
        description: 'Default tax rate percentage applied to new invoices and quotes',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'setting_key',
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving default tax rate:', error);
    throw new Error(error?.message || 'Failed to save default tax rate.');
  }
};

// Get enable due dates setting from database (defaults to true)
export const getEnableDueDates = async () => {
  try {
    const { data, error } = await supabase
      .from('application_settings')
      .select('setting_value')
      .eq('setting_key', 'enable_due_dates')
      .single();

    if (error) {
      // If setting doesn't exist, return default (true)
      if (error.code === 'PGRST116') {
        return true;
      }
      throw error;
    }

    return data?.setting_value === 'true' || data?.setting_value === true;
  } catch (error) {
    console.error('Error getting enable due dates setting:', error);
    // Fallback to true
    return true;
  }
};

// Save enable due dates setting to database (requires auth)
export const saveEnableDueDates = async (enable) => {
  await requireUser();
  try {
    const { error } = await supabase
      .from('application_settings')
      .upsert({
        setting_key: 'enable_due_dates',
        setting_value: enable.toString(),
        description: 'Enable or disable due dates on invoices and estimates',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'setting_key',
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving enable due dates setting:', error);
    throw new Error(error?.message || 'Failed to save due date setting.');
  }
};

// Get default due date offset (days from today) for new estimates/invoices. Default 0 = due today.
export const getDefaultDueDateOffsetDays = async () => {
  try {
    const { data, error } = await supabase
      .from('application_settings')
      .select('setting_value')
      .eq('setting_key', 'default_due_date_offset_days')
      .single();

    if (error) {
      if (error.code === 'PGRST116') return 0;
      throw error;
    }
    const val = parseInt(data?.setting_value, 10);
    return Number.isFinite(val) ? val : 0;
  } catch (error) {
    console.error('Error getting default due date offset:', error);
    return 0;
  }
};

// Save default due date offset (days). 0 = due today; do not default to tomorrow unless configured.
export const saveDefaultDueDateOffsetDays = async (days) => {
  await requireUser();
  try {
    const { error } = await supabase
      .from('application_settings')
      .upsert({
        setting_key: 'default_due_date_offset_days',
        setting_value: String(Math.max(0, parseInt(days, 10) || 0)),
        description: 'Default due date offset in days from document date (0 = due today)',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'setting_key',
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving default due date offset:', error);
    throw new Error(error?.message || 'Failed to save due date offset.');
  }
};